import { useCallback, useState } from 'react';

interface IUseSlideshowSoundControlParams {
    /**
     * Stores the new level. Owned by the caller, since where volume lives differs per surface —
     * Studio shares one persisted music-preview level, the client gallery keeps its own
     * per-player level.
     */
    setVolume: (volume: number) => void;
}

export interface IUseSlideshowSoundControlResult {
    isMuted: boolean;
    handleToggleMute: () => void;
    handleVolumeChange: (value: number) => void;
}

/**
 * Mute/volume behavior shared by the interactive slideshow players (`StudioSlideshowViewer`,
 * `ClientSlideshowViewer`).
 *
 * Mute is deliberately its own boolean rather than "volume set to 0": it silences the song
 * *without* losing the level the viewer chose, and `SlideshowPlayer`'s `muted` prop keeps the
 * song running against the slideshow timeline so unmuting picks up wherever the slides are
 * instead of restarting. Dragging the slider above zero unmutes, matching the video player.
 */
export function useSlideshowSoundControl({
    setVolume
}: IUseSlideshowSoundControlParams): IUseSlideshowSoundControlResult {
    const [isMuted, setIsMuted] = useState(false);

    const handleToggleMute = useCallback(() => {
        setIsMuted((muted) => !muted);
    }, []);

    const handleVolumeChange = useCallback(
        (value: number) => {
            setVolume(value);

            if (value > 0) {
                setIsMuted(false);
            }
        },
        [setVolume]
    );

    return { isMuted, handleToggleMute, handleVolumeChange };
}

export default useSlideshowSoundControl;
